/* Shared information disclosure. No PDP dependency. Duplicate module loads are safe. */
(() => {
  if (customElements.get('dlj-info')) return;
  let active = null;
  class DljInfo extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      // Dynamic clients may append the component's children after insertion.
      if (!this.querySelector('.dlj-info__panel')) {
        this.readyObserver = new MutationObserver(() => {
          if (this.querySelector('.dlj-info__panel')) { this.readyObserver.disconnect(); this.connectedCallback(); }
        });
        this.readyObserver.observe(this, {childList:true,subtree:true});
        return;
      }
      this.details = this.querySelector('details');
      this.trigger = this.querySelector('summary');
      this.panel = this.querySelector('.dlj-info__panel');
      if (!this.details || !this.trigger) return;
      this.controller = new AbortController();
      const options = {signal:this.controller.signal};
      this.nativePopover = typeof this.panel.showPopover === 'function';
      if (this.nativePopover) this.panel.setAttribute('popover','auto');
      this.details.addEventListener('toggle', () => {
        if (this.details.open) this.open(); else this.close();
      }, options);
      this.panel.addEventListener('toggle', event => {
        if (event.newState === 'closed' && this.details.open) this.close();
      }, options);
      document.addEventListener('pointerdown', event => {
        if (this.details.open && !this.contains(event.target)) this.close();
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.details.open) { event.preventDefault(); this.close(true); }
      }, options);
      document.addEventListener('focusin', event => {
        if (this.details.open && !this.contains(event.target)) this.close();
      }, options);
      window.addEventListener('resize', () => this.close(), options);
      // Close when content moves; scrolling inside a longer information panel remains possible.
      document.addEventListener('scroll', event => {
        if (!this.panel.contains(event.target)) this.close();
      }, {...options,capture:true,passive:true});
      if (this.details.open) this.open();
    }
    open() {
      if (!this.isConnected) return;
      if (active && active !== this) active.close();
      active = this;
      if (this.nativePopover && !this.panel.matches(':popover-open')) this.panel.showPopover();
      this.position();
      this.panel.setAttribute('data-open','');
    }
    position() {
      const viewport = window.visualViewport;
      const leftEdge = (viewport?.offsetLeft || 0) + 16;
      const topEdge = (viewport?.offsetTop || 0) + 16;
      const width = viewport?.width || document.documentElement.clientWidth;
      const height = viewport?.height || window.innerHeight;
      const rightEdge = leftEdge + width - 32;
      const bottomEdge = topEdge + height - 32;
      this.panel.style.width = Math.min(240,Math.max(0,width - 32)) + 'px';
      this.panel.style.setProperty('--dlj-info-height',Math.max(0,height - 32) + 'px');
      const anchor = (this.trigger.querySelector('svg') || this.trigger).getBoundingClientRect();
      const panel = this.panel.getBoundingClientRect();
      const left = Math.max(leftEdge,Math.min(anchor.left - 8,rightEdge - panel.width));
      // Prefer the space above the label to leave size buttons accessible below it.
      const above = anchor.top - panel.height - 6;
      const below = anchor.bottom + 6;
      const top = above >= topEdge ? above : Math.max(topEdge,Math.min(below,bottomEdge - panel.height));
      this.panel.style.setProperty('--dlj-info-left',left + 'px');
      this.panel.style.setProperty('--dlj-info-top',top + 'px');
      this.panel.setAttribute('data-positioned','');
    }
    close(restoreFocus = false) {
      if (!this.details) return;
      this.details.open = false;
      if (this.nativePopover && this.panel.matches(':popover-open')) this.panel.hidePopover();
      this.panel.removeAttribute('data-open');
      if (active === this) active = null;
      if (restoreFocus && this.trigger.isConnected) this.trigger.focus({preventScroll:true});
    }
    disconnectedCallback() {
      this.close();
      this.controller?.abort();
      this.controller = null;
      this.readyObserver?.disconnect();
    }
  }
  customElements.define('dlj-info',DljInfo);
})();
